// API Playground (production) — runnable, multi-endpoint, the replacement for the
// legacy docs ApiExample. Self-contained chrome, routed at /tools/api-playground.
// NPI runs LIVE against the public, no-auth GET /public/npi/{npi} endpoint. The
// other data types are authenticated-only, so they show representative responses
// (clearly labeled) with a free-key CTA — wire them as public endpoints ship.
import { useState, useCallback } from "react";
import { PageMeta } from "@/components/PageMeta";
import { canonicalUrl } from "@/lib/canonical";
import FieldHeader from "@/components/field/FieldHeader";
import FieldFooter from "@/components/field/FieldFooter";
import { trackGA4Event } from "@/lib/analytics";
import "@/styles/design-tokens.css";
import "./api-playground.css";

// Empty VITE_API_URL → relative paths through the dev proxy; otherwise direct.
const API_BASE = import.meta.env.VITE_API_URL ?? "https://devapi.fhirfly.io";

type Lang = "curl" | "ts" | "python";

interface Endpoint {
  id: string;
  label: string;
  sdkProp: string;
  prefix: string;
  example: string;
  paramLabel: string;
  log: string;
  /** Set when there's a public, no-auth endpoint that Run can call for real. */
  livePath?: string;
  response: (v: string) => unknown;
}

const ENDPOINTS: Endpoint[] = [
  { id: "npi", label: "NPI · Providers", sdkProp: "npi", prefix: "/v1/npi/", example: "1023011178", paramLabel: "NPI", log: "data.name.full_name, data.taxonomies[0].display_name",
    livePath: "/public/npi/",
    response: (v) => ({ data: { npi: v, entity_type: "organization", is_active: true, name: { full_name: "Cascade Family Health, PLLC" }, taxonomies: [{ code: "207Q00000X", display_name: "Family Medicine", primary: true }], practice_address: { city: "Bend", state: "OR", postal: "97702" } }, meta: { legal: { source_name: "CMS NPPES" } } }) },
  { id: "ndc", label: "NDC · Drugs", sdkProp: "ndc", prefix: "/v1/ndc/", example: "0071-0155", paramLabel: "NDC", log: "data.brand_name, data.generic_name",
    response: (v) => ({ data: { ndc: v, brand_name: "Lipitor", generic_name: "Atorvastatin Calcium", manufacturer: "Parke-Davis", dosage_form: "TABLET", route: "ORAL", strength: "10 mg" }, meta: { legal: { source_name: "FDA NDC Directory" } } }) },
  { id: "icd10", label: "ICD-10 · Diagnoses", sdkProp: "icd10", prefix: "/v1/icd10/", example: "E11.9", paramLabel: "Code", log: "data.description",
    response: (v) => ({ data: { code: v, description: "Type 2 diabetes mellitus without complications", chapter: "Endocrine, nutritional and metabolic diseases", billable: true }, meta: { legal: { source_name: "CMS ICD-10-CM" } } }) },
  { id: "rxnorm", label: "RxNorm · Clinical drugs", sdkProp: "rxnorm", prefix: "/v1/rxnorm/", example: "310965", paramLabel: "RXCUI", log: "data.name",
    response: (v) => ({ data: { rxcui: v, name: "Ibuprofen 200 MG Oral Tablet", tty: "SCD", ingredients: ["Ibuprofen"] }, meta: { legal: { source_name: "NLM RxNorm" } } }) },
  { id: "loinc", label: "LOINC · Lab tests", sdkProp: "loinc", prefix: "/v1/loinc/", example: "2339-0", paramLabel: "Code", log: "data.long_common_name",
    response: (v) => ({ data: { code: v, long_common_name: "Glucose [Mass/volume] in Blood", component: "Glucose", system: "Bld", property: "MCnc" }, meta: { legal: { source_name: "Regenstrief LOINC" } } }) },
  { id: "hcpcs", label: "HCPCS · Procedures", sdkProp: "hcpcs", prefix: "/v1/hcpcs/", example: "J1745", paramLabel: "Code", log: "data.description",
    response: (v) => ({ data: { code: v, description: "Injection, infliximab, 10 mg", category: "Drugs administered other than oral method", coverage: "Covered" }, meta: { legal: { source_name: "CMS HCPCS" } } }) },
];

const LANGS: { id: Lang; label: string }[] = [
  { id: "curl", label: "cURL" },
  { id: "ts", label: "TypeScript" },
  { id: "python", label: "Python" },
];

function statusText(status: number): string {
  const map: Record<number, string> = {
    200: "OK", 400: "Bad Request", 404: "Not Found", 429: "Too Many Requests", 500: "Server Error",
  };
  return map[status] ?? "";
}

function code(lang: Lang, ep: Endpoint, val: string): string {
  const url = `https://api.fhirfly.io${ep.prefix}${val}`;
  if (lang === "curl") return `curl "${url}" \\\n  -H "x-api-key: $FHIRFLY_KEY"`;
  if (lang === "ts")
    return `import { Fhirfly } from "@fhirfly-io/terminology";\n\nconst client = new Fhirfly({ apiKey: process.env.FHIRFLY_KEY });\nconst { data } = await client.${ep.sdkProp}.lookup("${val}");\n\nconsole.log(${ep.log});`;
  return `import requests\n\nr = requests.get(\n    "${url}",\n    headers={"x-api-key": FHIRFLY_KEY},\n)\nprint(r.json()["data"])`;
}

export default function ApiPlaygroundPage() {
  const [epId, setEpId] = useState(ENDPOINTS[0]!.id);
  const ep = ENDPOINTS.find((e) => e.id === epId)!;
  const [val, setVal] = useState(ep.example);
  const [lang, setLang] = useState<Lang>("curl");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ json: string; ms: number; status: number; live: boolean } | null>(null);
  const [copied, setCopied] = useState(false);

  const selectEndpoint = useCallback((id: string) => {
    const next = ENDPOINTS.find((e) => e.id === id)!;
    setEpId(id);
    setVal(next.example);
    setResult(null);
  }, []);

  const run = useCallback(async () => {
    setLoading(true);
    setResult(null);
    const started = Date.now();

    // Live path: real fetch against the public, no-auth endpoint.
    if (ep.livePath) {
      try {
        const res = await fetch(`${API_BASE}${ep.livePath}${encodeURIComponent(val.trim())}`);
        const body = await res.json().catch(() => ({}));
        setResult({ json: JSON.stringify(body, null, 2), ms: Date.now() - started, status: res.status, live: true });
        trackGA4Event("playground_run", { endpoint: ep.id, mode: "live", status: res.status });
      } catch {
        setResult({ json: JSON.stringify({ error: "network_error", message: "Could not reach the API." }, null, 2), ms: Date.now() - started, status: 0, live: true });
        trackGA4Event("playground_run", { endpoint: ep.id, mode: "live", status: 0 });
      } finally {
        setLoading(false);
      }
      return;
    }

    // Other data types are key-only: show a representative response, labeled.
    const jitter = 160 + ((val.length * 41) % 240);
    setTimeout(() => {
      setResult({ json: JSON.stringify(ep.response(val), null, 2), ms: Date.now() - started, status: 200, live: false });
      setLoading(false);
      trackGA4Event("playground_run", { endpoint: ep.id, mode: "sample", status: 200 });
    }, jitter);
  }, [ep, val]);

  const copy = useCallback(() => {
    navigator.clipboard?.writeText(code(lang, ep, val)).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [lang, ep, val]);

  return (
    <div className="ffly ffly--public api-playground">
      <PageMeta
        title="API Playground — try the FHIRfly healthcare API live"
        description="Run real FHIRfly API calls in your browser — NPI, NDC, ICD-10, RxNorm, LOINC, HCPCS and more. Pick an endpoint, hit Run, see the live response. No sign-up required."
        canonical={canonicalUrl("/tools/api-playground")}
      />
      <FieldHeader active="tools" />

      <div className="wrap">
        <section className="intro">
          <div className="plate">✛ Live API · try it before you sign up</div>
          <h1>API Playground</h1>
          <p>Pick an endpoint, edit the value, hit Run, and see the real response. No account needed — this is the API itself, not a screenshot of it.</p>
        </section>

        <div className="console">
          <div className="endpoint-row">
            <span className="lbl">Endpoint</span>
            <select className="endpoint-select" value={epId} onChange={(e) => selectEndpoint(e.target.value)} aria-label="Choose an endpoint">
              {ENDPOINTS.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
            </select>
            <span className="count">+ 15 more data types →</span>
          </div>

          <div className="reqbar">
            <span className="method">GET</span>
            <div className="pathwrap">
              <span className="base">https://api.fhirfly.io</span>
              <span className="seg">{ep.prefix}</span>
              <span className="param"><input value={val} onChange={(e) => setVal(e.target.value)} aria-label={ep.paramLabel} /></span>
            </div>
            <button className="run" onClick={run} disabled={loading}>
              {loading ? <><span className="spin" />Running</> : <>▶ Run</>}
            </button>
          </div>

          <div className="panes">
            <div className="pane left">
              <div className="tabs">
                {LANGS.map((l) => (
                  <button key={l.id} className={`tab${lang === l.id ? " active" : ""}`} onClick={() => setLang(l.id)}>{l.label}</button>
                ))}
              </div>
              <div className="panehead">
                <span className="lbl">Request</span>
                <button className="copy" onClick={copy}>{copied ? "Copied!" : "Copy"}</button>
              </div>
              <pre>{code(lang, ep, val)}</pre>
            </div>
            <div className="pane">
              <div className="panehead">
                <span className="lbl">Response</span>
                <span className="status">
                  {result ? (
                    <>
                      <span className={result.status >= 200 && result.status < 300 ? "ok" : "err"}>
                        {result.status === 0 ? "Network error" : `${result.status} ${statusText(result.status)}`}
                      </span>
                      <span className="ms">{result.ms} ms</span>
                      <span className={`srcbadge ${result.live ? "live" : "sample"}`}>{result.live ? "live" : "sample"}</span>
                    </>
                  ) : <span className="ms">—</span>}
                </span>
              </div>
              {result ? <pre className="resp">{result.json}</pre> : <div className="empty">{loading ? "Calling the API…" : ep.livePath ? "Press Run to call the live endpoint" : "Press Run to see a sample response"}</div>}
            </div>
          </div>
        </div>

        <div className="bridge">
          <div className="pitch">No sign-up, no key — this calls the live public API. A free key unlocks all 21+ data types, batch lookups, and search.</div>
          <a
            className="dev-cta"
            href="/dashboard"
            onClick={() => trackGA4Event("tool_cta_click", { cta: "get_api_key", location: "api_playground" })}
          >Get your own key — free →</a>
        </div>

        <p className="note"><b>NPI runs live</b> against the public, no-auth endpoint (rate-limited per IP). The other data types are key-only, so they show a representative response (marked <span className="srcbadge sample">sample</span>) — grab a free key to call all 21+ for real.</p>
      </div>

      <FieldFooter variant="full" />
    </div>
  );
}
